import React, { useEffect, useState } from 'react';
import { useAuth } from '../../shared/contexts/AuthContext';
import SettingsSidebar from '../sidebars/SettingsSidebar';
import Modal from './Modal';
import { useTranslation } from 'react-i18next';
import api from '../../shared/api/axios';
/* ----------------------------- types/api utils ---------------------------- */

type TwoFAStatusResponse = { twoFA: { enabled: boolean; method: 'email' | null } };
type EnableResponse = { msg: string; recoveryCodes: string[] };
type GenericOk = { ok?: boolean; msg?: string };
type ErrorWithResponse = {
  response?: { status?: number; data?: any };
  message?: string;
};

type ErrorWithPayload = {
  status?: number;
  message?: string;
};

const emailOk = (v: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v);


/* ---------------------------------- UI bits -------------------------------- */

const Button: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({
  children,
  style,
  ...rest
}) => (
  <button
    {...rest}
    style={{
      padding: '8px 12px',
      borderRadius: 999,
      border: '1px solid rgba(148,163,184,0.5)',
      background: '#111827',
      color: '#e5e7eb',
      fontSize: 13,
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      ...style,
    }}
  >
    {children}
  </button>
);

type SwitchProps = { checked: boolean; onCheckedChange?: (value: boolean) => void };
const Switch: React.FC<SwitchProps> = ({ checked, onCheckedChange }) => (
  <input
    aria-label="toggle"
    type="checkbox"
    checked={!!checked}
    onChange={(e) => onCheckedChange?.(e.target.checked)}
    style={{
      width: 40,
      height: 20,
      cursor: 'pointer',
      accentColor: '#22c55e',
    }}
  />
);

/* --------------------------- 2FA wizard (modal) --------------------------- */

enum Step {
  Password = 1,
  Email = 2,
  OTP = 3,
  Done = 4,
}

const TwoFAWizardModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onEnabled: (recoveryCodes: string[]) => void;
}> = ({ open, onClose, onEnabled }) => {
  const { t } = useTranslation();

  const [step, setStep] = useState<Step>(Step.Password);

  // fields
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [email2, setEmail2] = useState('');
  const [otp, setOtp] = useState('');

  // UX
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // reset when closed
  useEffect(() => {
    if (!open) {
      setStep(Step.Password);
      setPassword('');
      setEmail('');
      setEmail2('');
      setOtp('');
      setError('');
      setBusy(false);
    }
  }, [open]);

  const nextFromPassword = () => {
    setError('');
    if (!password) return setError(t('securitySettings.twoFAWizard.errors.missingPassword'));
    setStep(Step.Email);
  };

  const sendSetupCode = async () => {
    setError('');
    if (!email || !email2) {
      return setError(t('securitySettings.twoFAWizard.errors.missingEmails'));
    }
    if (email !== email2) {
      return setError(t('securitySettings.twoFAWizard.errors.emailMismatch'));
    }
    if (!emailOk(email)) {
      return setError(t('securitySettings.twoFAWizard.errors.invalidEmail'));
    }

    setBusy(true);
    try {
      await api.post('/security/2fa/email/request-setup', {
        currentPassword: password,
        targetEmail: email,
      });
      setStep(Step.OTP);
    } catch (e: any) {
      const err = e as ErrorWithResponse;
      const status = err?.response?.status;
      const baseMsg =
        err?.response?.data?.msg ||
        err?.message ||
        t('securitySettings.twoFAWizard.errors.sendFailed');

      setError(
        status === 401
          ? `${baseMsg} ${t('securitySettings.twoFAWizard.errors.unauthenticatedSuffix')}`
          : baseMsg
      );
    } finally {

      setBusy(false);
    }
  };
  const verifyAndEnable = async () => {
    setError('');
    const code = otp.trim();
    if (!/^\d{6}$/.test(code)) {
      return setError(t('securitySettings.twoFAWizard.errors.missingOtp'));
    }

    setBusy(true);
    try {
      const res = await api.post<EnableResponse>('/security/2fa/email/enable', { otp: code });
      onEnabled(res.data?.recoveryCodes || []);
      setStep(Step.Done);
    } catch (e: any) {
      const err = e as ErrorWithResponse;
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.msg ||
        err?.message ||
        t('securitySettings.twoFAWizard.errors.sendFailed');

      setError(
        status === 401
          ? `${msg} ${t('securitySettings.twoFAWizard.errors.unauthenticatedSuffix')}`
          : msg
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('securitySettings.twoFAWizard.modalTitle')}
      width={520}
    >
      {error && (
        <div
          style={{
            background: '#451a0a',
            border: '1px solid #f97316',
            color: '#fed7aa',
            padding: 10,
            borderRadius: 8,
            marginBottom: 10,
          }}
        >
          {error}
        </div>
      )}

      {step === Step.Password && (
        <div>
          <p style={{ marginBottom: 8, fontSize: 13, color: '#e5e7eb' }}>
            {t('securitySettings.twoFAWizard.steps.passwordTitle')}
          </p>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('securitySettings.twoFAWizard.steps.passwordPlaceholder')}
            autoComplete="current-password"
            style={modalInputStyle}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
            <Button onClick={onClose}>
              {t('securitySettings.twoFAWizard.buttons.cancel')}
            </Button>
            <Button onClick={nextFromPassword}>
              {t('securitySettings.twoFAWizard.buttons.continue')}
            </Button>
          </div>
        </div>
      )}

      {step === Step.Email && (
        <div>
          <p style={{ marginBottom: 8, fontSize: 13, color: '#e5e7eb' }}>
            {t('securitySettings.twoFAWizard.steps.emailTitle')}
          </p>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('securitySettings.twoFAWizard.steps.emailPlaceholder')}
            style={{ ...modalInputStyle, marginBottom: 8 }}
          />
          <input
            type="email"
            value={email2}
            onChange={(e) => setEmail2(e.target.value)}
            placeholder={t('securitySettings.twoFAWizard.steps.emailConfirmPlaceholder')}
            style={modalInputStyle}
          />
          <div
            style={{
              display: 'flex',
              gap: 8,
              marginTop: 12,
              justifyContent: 'space-between',
            }}
          >
            <Button onClick={() => setStep(Step.Password)}>
              {t('securitySettings.twoFAWizard.buttons.back')}
            </Button>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button onClick={onClose}>
                {t('securitySettings.twoFAWizard.buttons.cancel')}
              </Button>
              <Button onClick={sendSetupCode} disabled={busy}>
                {busy
                  ? t('securitySettings.twoFAWizard.buttons.sendCodeBusy')
                  : t('securitySettings.twoFAWizard.buttons.sendCodeIdle')}
              </Button>
            </div>
          </div>
          <small style={{ display: 'block', marginTop: 8, color: '#9ca3af' }}>
            {t('securitySettings.twoFAWizard.steps.emailTip')}
          </small>
        </div>
      )}

      {step === Step.OTP && (
        <div>
          <p style={{ marginBottom: 8, fontSize: 13, color: '#e5e7eb' }}>
            {t('securitySettings.twoFAWizard.steps.otpTitlePrefix')}{' '}
            <strong>{email}</strong>
          </p>
          <input
            inputMode="numeric"
            pattern="\d*"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            placeholder={t('securitySettings.twoFAWizard.steps.otpPlaceholder')}
            maxLength={6}
            style={modalInputStyle}
          />
          <div
            style={{
              display: 'flex',
              gap: 8,
              marginTop: 12,
              justifyContent: 'space-between',
            }}
          >
            <Button onClick={() => setStep(Step.Email)}>
              {t('securitySettings.twoFAWizard.buttons.back')}
            </Button>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button onClick={onClose}>
                {t('securitySettings.twoFAWizard.buttons.cancel')}
              </Button>
              <Button onClick={verifyAndEnable} disabled={busy}>
                {busy
                  ? t('securitySettings.twoFAWizard.buttons.verifyEnableBusy')
                  : t('securitySettings.twoFAWizard.buttons.verifyEnableIdle')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {step === Step.Done && (
        <div>
          <p style={{ marginBottom: 0, fontSize: 13, color: '#bbf7d0' }}>
            {t('securitySettings.twoFAWizard.steps.doneMessage')}
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
            <Button onClick={onClose}>
              {t('securitySettings.twoFAWizard.buttons.close')}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

/* ---------------------------- Main component --------------------------- */

const SecuritySettings: React.FC = () => {
  const { isAuthenticated, logout } = useAuth();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);

  const [disableOpen, setDisableOpen] = useState(false);
  const [pwForDisable, setPwForDisable] = useState('');

  const [wizardOpen, setWizardOpen] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  const [submittingDisable, setSubmittingDisable] = useState(false);
  const [revokingAll, setRevokingAll] = useState(false);
  const [error, setError] = useState('');
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!isAuthenticated) throw new Error('Not authenticated');
        const res = await api.get<TwoFAStatusResponse>('/security/2fa/status');
        if (!mounted) return;
        setTwoFAEnabled(!!res.data?.twoFA?.enabled);
      } catch (e: any) {
        const err = e as ErrorWithResponse;
        const msg =
          err?.response?.data?.msg ||
          err?.message ||
          t('securitySettings.loadError.statusLoadFallback');
      
        setError(msg);
      }   
       finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [isAuthenticated, t]);

  async function handleDisable() {
    setError('');
    if (!pwForDisable.trim()) {
      setError(t('securitySettings.twoFA.alerts.disableMissingPassword'));
      return;
    }
    setSubmittingDisable(true);
    try {
      await api.post<GenericOk>('/security/2fa/disable', {
        currentPassword: pwForDisable.trim(),
      });      
      setTwoFAEnabled(false);
      setDisableOpen(false);
      setPwForDisable('');
      alert(t('securitySettings.twoFA.alerts.disableSuccess'));
    } catch (e) {
      const msg =
        (e as ErrorWithPayload)?.message ||
        t('securitySettings.twoFA.alerts.disableError');
      setError(msg);
    } finally {
      setSubmittingDisable(false);
    }
  }
async function handleLogoutAllConfirmed() {
  setError('');
  setRevokingAll(true);

  try {
    await api.post('/security/logout-all', {});
    await logout();

    alert(t('securitySettings.twoFA.alerts.logoutAllSuccess'));
  } catch (e) {
    const msg =
      (e as ErrorWithPayload)?.message ||
      t('securitySettings.twoFA.alerts.logoutAllError');

    setError(msg);
  } finally {
    setRevokingAll(false);
    setLogoutConfirmOpen(false);
  }
}


  const downloadRecovery = () => {
    if (!recoveryCodes?.length) return;
    const header = t('securitySettings.recoveryCodes.fileHeader');
    const generatedLabel = t('securitySettings.recoveryCodes.fileGeneratedLabel');
    const content =
      `${header}\n${generatedLabel} ${new Date().toISOString()}\n\n` +
      recoveryCodes.join('\n') +
      '\n';
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#02040b' }}>
      {/* Left fixed settings sidebar */}
      <SettingsSidebar />

      <main
        style={{
          marginLeft: 300,
          padding: 24,
          width: '100%',
          maxWidth: 960,
          boxSizing: 'border-box',
          color: '#e5e7eb',
        }}
      >
        <h2
          style={{
            fontSize: 24,
            fontWeight: 700,
            marginBottom: 12,
          }}
        >
          {t('securitySettings.page.title')}
        </h2>
        <p style={{ color: '#9ca3af', fontSize: 13, marginBottom: 16 }}>
          {t('securitySettings.page.subtitle')}
        </p>

        {error && (
          <div
            style={{
              background: '#451a0a',
              border: '1px solid #f97316',
              color: '#fed7aa',
              padding: 10,
              borderRadius: 8,
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        )}

        {/* 2FA card */}
        <section
          style={{
            borderRadius: 12,
            border: '1px solid rgba(148,163,184,0.4)',
            background: 'rgba(15,23,42,0.9)',
            padding: 16,
            marginBottom: 16,
          }}
        >
          {loading ? (
            <p style={{ fontSize: 13, color: '#9ca3af' }}>
              {t('securitySettings.twoFA.loading')}
            </p>
          ) : (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 8,
                }}
              >
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>
                    {t('securitySettings.twoFA.sectionTitle')}
                  </div>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>
                    {t('securitySettings.twoFA.statusLabel')}{' '}
                    <span
                      style={{
                        fontWeight: 600,
                        color: twoFAEnabled ? '#22c55e' : '#f97316',
                      }}
                    >
                      {twoFAEnabled
                        ? t('securitySettings.twoFA.statusEnabled')
                        : t('securitySettings.twoFA.statusDisabled')}
                    </span>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                <Button onClick={() => setWizardOpen(true)}>
                  {t('securitySettings.twoFA.manageButton')}
                </Button>
                {twoFAEnabled && (
                  <Button
                    onClick={() => setDisableOpen(true)}
                    style={{
                      background: '#7f1d1d',
                      borderColor: '#b91c1c',
                      color: '#fee2e2',
                    }}
                  >
                    {t('securitySettings.twoFA.disableButton')}
                  </Button>
                )}
              </div>

              {!twoFAEnabled && (
                <small style={{ display: 'block', marginTop: 8, color: '#9ca3af' }}>
                  {t('securitySettings.twoFA.enableHint')}
                </small>
              )}
            </>
          )}
        </section>

        {/* Recovery codes banner */}
        {Array.isArray(recoveryCodes) && recoveryCodes.length > 0 && (
          <section
            style={{
              borderRadius: 12,
              border: '1px solid rgba(148,163,184,0.4)',
              background: '#020617',
              padding: 16,
              marginBottom: 16,
            }}
          >
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
              {t('securitySettings.recoveryCodes.sectionTitle')}
            </h3>
            <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 8 }}>
              {t('securitySettings.recoveryCodes.description')}
            </p>
            <pre
              style={{
                background: '#020617',
                color: '#22c55e',
                padding: 12,
                borderRadius: 8,
                border: '1px solid rgba(34,197,94,0.4)',
                fontSize: 12,
                overflowX: 'auto',
                marginBottom: 8,
              }}
            >
{recoveryCodes.join('\n')}
            </pre>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button onClick={downloadRecovery}>
                {t('securitySettings.recoveryCodes.downloadButton')}
              </Button>
              <Button
                onClick={() =>
                  navigator.clipboard
                    .writeText(recoveryCodes.join('\n'))
                    .then(
                      () => alert(t('securitySettings.recoveryCodes.copySuccess')),
                      () => alert(t('securitySettings.recoveryCodes.copyError'))
                    )
                }
              >
                {t('securitySettings.recoveryCodes.copyButton')}
              </Button>
              <Button
                onClick={() => setRecoveryCodes(null)}
                style={{ background: '#111827', borderColor: 'rgba(148,163,184,0.5)' }}
              >
                {t('securitySettings.recoveryCodes.dismissButton')}
              </Button>
            </div>
          </section>
        )}

        {/* Sessions card */}
        <section
          style={{
            borderRadius: 12,
            border: '1px solid rgba(148,163,184,0.4)',
            background: 'rgba(15,23,42,0.9)',
            padding: 16,
          }}
        >
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
            {t('securitySettings.sessions.sectionTitle')}
          </h3>
          <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 8 }}>
            {t('securitySettings.sessions.description')}
          </p>
          <Button onClick={() => setLogoutConfirmOpen(true)}>

            {revokingAll
              ? t('securitySettings.sessions.buttonBusy')
              : t('securitySettings.sessions.buttonIdle')}
          </Button>
        </section>
      </main>

      {/* Disable modal */}
      <Modal
        open={disableOpen}
        onClose={() => setDisableOpen(false)}
        title={t('securitySettings.disableModal.title')}
        width={480}
      >
        <p style={{ marginBottom: 10, fontSize: 13, color: '#e5e7eb' }}>
          {t('securitySettings.disableModal.description')}
        </p>
        <div style={{ marginBottom: 12 }}>
          <label
            htmlFor="pw-disable"
            style={{ fontSize: 13, color: '#9ca3af', display: 'block' }}
          >
            {t('securitySettings.disableModal.passwordLabel')}
          </label>
          <input
            id="pw-disable"
            type="password"
            value={pwForDisable}
            onChange={(e) => setPwForDisable(e.target.value)}
            placeholder={t('securitySettings.disableModal.passwordPlaceholder')}
            autoComplete="current-password"
            style={modalInputStyle}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
          <Button onClick={() => setDisableOpen(false)}>
            {t('securitySettings.disableModal.cancelButton')}
          </Button>
          <Button
            onClick={handleDisable}
            disabled={submittingDisable}
            style={{
              background: '#7f1d1d',
              borderColor: '#b91c1c',
              color: '#fee2e2',
            }}
          >
            {submittingDisable
              ? t('securitySettings.disableModal.confirmButtonBusy')
              : t('securitySettings.disableModal.confirmButtonIdle')}
          </Button>
        </div>
      </Modal>

      <Modal
  open={logoutConfirmOpen}
  onClose={() => setLogoutConfirmOpen(false)}
  title={t('securitySettings.sessions.confirmTitle')}
  width={480}
>
  <p style={{ fontSize: 13, color: '#e5e7eb', marginBottom: 14 }}>
    {t('securitySettings.twoFA.alerts.logoutAllConfirm')}
  </p>

  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
    <Button onClick={() => setLogoutConfirmOpen(false)}>
      {t('common.cancel')}
    </Button>

    <Button
      onClick={handleLogoutAllConfirmed}
      disabled={revokingAll}
      style={{
        background: '#7f1d1d',
        borderColor: '#b91c1c',
        color: '#fee2e2',
      }}
    >
      {revokingAll
        ? t('securitySettings.sessions.buttonBusy')
        : t('securitySettings.sessions.buttonIdle')}
    </Button>
  </div>
</Modal>

  {/* Enable wizard modal */}
  <TwoFAWizardModal
  open={wizardOpen}
  onClose={() => setWizardOpen(false)}
  onEnabled={(codes) => {
    setTwoFAEnabled(true);
    setRecoveryCodes(codes || []);
  }}
/>
  </div>
  );
};

const modalInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px',
  marginTop: 6,
  borderRadius: 8,
  border: '1px solid rgba(148,163,184,0.6)',
  fontSize: 14,
  boxSizing: 'border-box',
  background: '#020617',
  color: '#e5e7eb',
};

export default SecuritySettings;
